import React, { useState } from "react";
import styled from "styled-components";
import VerticalGradient from "./components/VerticalGradient";
import HorizontalGradient from "./components/HorizontalGradient";
import CircularGradient from "./components/CircularGradient";
import LoadingDots from "./components/LoadingDots";

// Color variables
const white = "#ffffff";
const black = "#000000";
const inputBorderGrey = "#333333";
const buttonBlue = "#007bff";
const buttonHoverBlue = "#0056b3";

// Gradient colors
const gradientStart = "#A7C7E7";
const gradientEnd = "#093054";

// Styled Components
const AppContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: linear-gradient(to bottom, ${gradientStart}, ${gradientEnd});
  * {
    user-select: text;
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 800px;
  width: 80%;
  margin: 0 auto;
  padding-left: 10px;
  padding-right: 10px;
  padding-bottom: 10px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const Message = styled.div`
  padding: 10px 15px;
  border-radius: 8px;
  margin-bottom: 10px;
  max-width: 70%;
  word-wrap: break-word;
  line-height: 1.4;
  position: relative;
  z-index: 2;
  pointer-events: auto;
`;

const InputContainer = styled.form`
  position: relative;
  width: 100%;
`;

const MessageInput = styled.textarea`
  width: calc(100% - 20px);
  height: 24px;
  min-height: 24px;
  padding: 10px 12px;
  margin-bottom: 10px;
  font-size: 16px;
  background-color: ${white}33;
  color: ${black};
  resize: none;
  line-height: 1.5;
  overflow: hidden;
  font-family: "Inter", sans-serif;
  border-radius: 20px;
  border-width: 0px;
  &:focus {
    outline: none;
  }
`;

const SendButton = styled.button`
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background-image: url("/arrow_up.png");
  background-color: transparent;
  background-size: 24px;
  background-position: center;
  background-repeat: no-repeat;
  border: none;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0 : 1)};
  transition: opacity 0.5s ease-in-out;

  &:hover {
    background-color: ${buttonHoverBlue};
  }
`;

// Constants
export const serverUrl =
  process.env.NODE_ENV === "production"
    ? "https://phil-knight.vercel.app"
    : "http://localhost:3001";

const AIMessage = styled(Message)`
  margin-left: 0;
  background-color: transparent;
  color: ${black};
  z-index: 1;
`;

const UserMessage = styled(Message)`
  margin-left: auto;
  background-color: ${white}55;
  color: ${black};
  z-index: 1;
`;

// Styled Components
const Title = styled.h1`
  text-align: center;
  color: ${black}88;
  margin-top: 20px;
  user-select: none;
`;

const TitleSpan = styled.span`
  cursor: pointer;
  &:hover {
    color: ${black};
  }
`;

const LoadingDotsContainer = styled.div`
  margin-left: -25%;
  margin-top: 20px;
  padding: 10px 15px;
  border-radius: 8px;
  max-width: 70%;
  word-wrap: break-word;
  line-height: 1.4;
`;

const PullBlogButton = styled.button`
  padding: 10px 20px;
  background-color: ${white}88;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${white}cc;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const ToggleSlider = styled.span<{ $isOn: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => (props.$isOn ? buttonBlue : inputBorderGrey)};
  border-radius: 10px;
  transition: 0.4s;

  &:before {
    content: "";
    position: absolute;
    height: 16px;
    width: 16px;
    left: ${(props) => (props.$isOn ? "22px" : "2px")};
    bottom: 2px;
    background-color: ${white};
    border-radius: 8px;
    transition: 0.4s;
  }
`;

// Types
interface Message {
  content: string;
  isAI: boolean;
}

// Main Component
function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDotsCount, setLoadingDotsCount] = useState(3);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim() === "" || loading) return;

    const userMessage = { content: inputMessage, isAI: false };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setLoading(true);

    try {
      const response = await fetch(`${serverUrl}/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
        }),
      });
      if (!response.ok) {
        throw new Error("Server returned " + response.status);
      }

      const data = await response.json();

      if (!data.reply) {
        throw new Error("Got empty server response");
      }

      const aiMessage = {
        content: data.reply,
        isAI: true,
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error:", error);
      setMessages((prev) => [
        ...prev,
        {
          content: String("Sorry it seems like somethings wrong!  " + error),
          isAI: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppContainer>
      <ChatContainer>
        <Title>Chat with Phil Knight</Title>
        <MessagesContainer>
          {messages.map((message, index) =>
            message.isAI ? (
              <AIMessage key={index}>{message.content}</AIMessage>
            ) : (
              <UserMessage key={index}>{message.content}</UserMessage>
            )
          )}
          {loading && (
            <LoadingDotsContainer>
              <LoadingDots
                initialDotCount={loadingDotsCount}
                onDotsCountChange={setLoadingDotsCount}
              />
            </LoadingDotsContainer>
          )}
        </MessagesContainer>
        <InputContainer onSubmit={handleSubmit}>
          <MessageInput
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Message"
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (inputMessage.trim()) {
                  handleSubmit(e);
                }
              }
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "24px";
              const newHeight = target.scrollHeight;
              if (newHeight > 48) {
                target.style.height = newHeight + "px";
              }
            }}
          />
          <SendButton type="submit" disabled={!inputMessage.trim()} />
        </InputContainer>
      </ChatContainer>
    </AppContainer>
  );
}

export default App;
